import { useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { HAL, useIndeling } from './indeling'
import { useAnimatie, useObjecten } from '../scene/store'

function vul(mesh: THREE.InstancedMesh | null, m: THREE.Matrix4[]) {
  if (!mesh) return
  m.forEach((mat, i) => mesh.setMatrixAt(i, mat))
  mesh.instanceMatrix.needsUpdate = true
  mesh.computeBoundingSphere()
}

/**
 * Plafondconstructie: spanten, gordingen, sprinklerleiding met koppen, kabelgoten
 * en luchtkanalen met draaiende ventilatoren. Het plafond was een leeg vlak; dit
 * is de grootste diepte-winst per bestede regel, want het geeft de hal schaal.
 */
export function Plafond() {
  const ind = useIndeling()
  const obj = useObjecten()
  const anim = useAnimatie()

  const spanten = useRef<THREE.InstancedMesh>(null)
  const gordingen = useRef<THREE.InstancedMesh>(null)
  const diagonalen = useRef<THREE.InstancedMesh>(null)
  const leiding = useRef<THREE.InstancedMesh>(null)
  const koppen = useRef<THREE.InstancedMesh>(null)
  const goten = useRef<THREE.InstancedMesh>(null)
  const ventilatoren = useRef<THREE.Group>(null)

  const opbouw = useMemo(() => {
    const d = new THREE.Object3D()
    const spantM: THREE.Matrix4[] = []
    const gordM: THREE.Matrix4[] = []
    const diagM: THREE.Matrix4[] = []
    const leidM: THREE.Matrix4[] = []
    const kopM: THREE.Matrix4[] = []
    const gootM: THREE.Matrix4[] = []
    const zet = (a: THREE.Matrix4[]) => { d.updateMatrix(); a.push(d.matrix.clone()) }

    const y = HAL.hoogte - 0.25

    // Spanten boven de stellingruggen, dus op de gangpadmaat (5,6 m) en niet op
    // een eigen raster van 6 m. Dat is ook waar ze in het echt liggen — de
    // kolommen staan in de stellingrij, niet in het gangpad — en het houdt ze
    // weg onder de daklichten, die boven de gangpaden liggen (`daglicht.ts`).
    // Op 6 m hing er een spant pal onder twee van de vijf lichtstraten.
    const spantAfstand = ind.gangpadZ(1) - ind.gangpadZ(0) || 5.6
    const eersteSpant = ind.gangpadZ(0) + spantAfstand / 2
    const spantZ: number[] = []
    for (let z = eersteSpant; z >= -HAL.diepte * 0.85; z -= spantAfstand) spantZ.unshift(z)
    for (let z = eersteSpant + spantAfstand; z <= HAL.diepte * 0.85; z += spantAfstand) spantZ.push(z)
    for (const z of spantZ) {
      d.position.set(0, y, z); d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
      zet(spantM)
      // Vakwerk onder het spant
      for (let x = -HAL.breedte * 0.8; x <= HAL.breedte * 0.8; x += 2.4) {
        d.position.set(x, y - 0.42, z)
        d.rotation.set(0, 0, ((x / 2.4) % 2 === 0 ? 1 : -1) * 0.9)
        d.scale.set(1, 1, 1)
        zet(diagM)
      }
    }

    // Gordingen in de lengte
    for (let x = -HAL.breedte * 0.8; x <= HAL.breedte * 0.8; x += 4.5) {
      d.position.set(x, y + 0.3, 0); d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
      zet(gordM)
    }

    // Sprinklerleiding boven elk gangpad, met koppen om de 3 meter
    if (obj.sprinklers) {
      for (let i = 0; i < ind.cfg.gangpaden; i++) {
        const z = ind.gangpadZ(i)
        d.position.set(0, y - 0.75, z); d.rotation.set(0, 0, Math.PI / 2); d.scale.set(1, 1, 1)
        zet(leidM)
        for (let x = -HAL.breedte * 0.42; x <= HAL.breedte * 0.42; x += 3) {
          d.position.set(x, y - 0.92, z); d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
          zet(kopM)
        }
      }
    }

    // Kabelgoten langs de lengte-as
    if (obj.kabelgoten) {
      for (const z of [-HAL.diepte * 0.5, HAL.diepte * 0.5]) {
        d.position.set(0, y - 0.55, z); d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
        zet(gootM)
      }
    }

    return { spantM, gordM, diagM, leidM, kopM, gootM }
  }, [ind, obj.sprinklers, obj.kabelgoten])

  useLayoutEffect(() => {
    vul(spanten.current, opbouw.spantM)
    vul(gordingen.current, opbouw.gordM)
    vul(diagonalen.current, opbouw.diagM)
    vul(leiding.current, opbouw.leidM)
    vul(koppen.current, opbouw.kopM)
    vul(goten.current, opbouw.gootM)
  }, [opbouw])

  // Doorsnede: kijkt de camera van boven (Connectiviteit, de zweefroute), dan
  // verdwijnt de plafondconstructie. Het dak zelf is eenzijdig en valt van
  // boven al weg, maar de spanten en gordingen lagen er als een raster over
  // het hele beeld. Een maquette haal je ook het dak af, niet alleen de pannen.
  const geheel = useRef<THREE.Group>(null)
  useFrame(({ camera }) => {
    if (geheel.current) geheel.current.visible = camera.position.y < HAL.hoogte - 0.2
  })

  useFrame((_, dt) => {
    if (!ventilatoren.current || !anim.aan || !anim.ventilatoren) return
    ventilatoren.current.children.forEach((v, i) => {
      v.rotation.y += dt * (1.6 + i * 0.25) * anim.snelheid
    })
  })

  if (!obj.plafond) return null

  const staal = { color: '#3a4049', roughness: 0.52, metalness: 0.68 }

  return (
    <group ref={geheel}>
      <instancedMesh ref={spanten} args={[null!, null!, opbouw.spantM.length]} castShadow>
        <boxGeometry args={[HAL.breedte * 1.7, 0.42, 0.2]} />
        <meshStandardMaterial {...staal} />
      </instancedMesh>

      {/* Gordingen en vakwerk werpen schaduw: door de daklichten snijden ze de
          pauzes in de zonvlekken en in de lichtschachten. */}
      <instancedMesh ref={gordingen} args={[null!, null!, opbouw.gordM.length]} castShadow>
        <boxGeometry args={[0.16, 0.22, HAL.diepte * 1.75]} />
        <meshStandardMaterial {...staal} />
      </instancedMesh>

      <instancedMesh ref={diagonalen} args={[null!, null!, opbouw.diagM.length]} castShadow>
        <boxGeometry args={[0.06, 1.05, 0.06]} />
        <meshStandardMaterial {...staal} roughness={0.6} />
      </instancedMesh>

      <instancedMesh ref={leiding} args={[null!, null!, opbouw.leidM.length]}>
        <cylinderGeometry args={[0.07, 0.07, HAL.breedte * 0.9, 10]} />
        <meshStandardMaterial color="#7d2b2b" roughness={0.42} metalness={0.5} />
      </instancedMesh>

      <instancedMesh ref={koppen} args={[null!, null!, opbouw.kopM.length]}>
        <cylinderGeometry args={[0.035, 0.05, 0.14, 8]} />
        <meshStandardMaterial color="#b9a06a" roughness={0.35} metalness={0.7} />
      </instancedMesh>

      <instancedMesh ref={goten} args={[null!, null!, opbouw.gootM.length]}>
        <boxGeometry args={[HAL.breedte * 1.5, 0.12, 0.3]} />
        <meshStandardMaterial color="#4a5058" roughness={0.6} metalness={0.5} />
      </instancedMesh>

      {/* Luchtkanalen met draaiende ventilatoren */}
      <group ref={ventilatoren}>
        {[-20, 0, 20].map((x) => (
          <group key={x} position={[x, HAL.hoogte - 1.5, -HAL.diepte * 0.62]}>
            <mesh>
              <cylinderGeometry args={[0.62, 0.62, 0.3, 16]} />
              <meshStandardMaterial color="#555c66" roughness={0.5} metalness={0.6} />
            </mesh>
            {[0, 1, 2, 3].map((b) => (
              <mesh key={b} rotation={[0, (b * Math.PI) / 2, 0.35]} position={[0, 0.02, 0]}>
                <boxGeometry args={[0.52, 0.02, 0.17]} />
                <meshStandardMaterial color="#79818c" roughness={0.45} metalness={0.5} />
              </mesh>
            ))}
          </group>
        ))}
      </group>
    </group>
  )
}
